import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Search, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ListTools } from "@/components/ListToolbar";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import { Badge } from "@/components/ui";
import { listLots, lotDetailQuery, lotListQuery, type LotRow } from "@/features/lots/api";
import { LotPanel } from "@/features/lots/LotPanel";
import { useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { EXPORT_PAGE, type ExportColumn } from "@/lib/exportXlsx";
import { todayIso, useFormat } from "@/lib/format";
import { lotListColumns } from "@patronix/domain/list";
import { listView, noView, viewQuery, viewSearch, type ViewSearch } from "@/lib/listView";

// Lots (master data): every lot of the tenant with its on hand in the active
// company. Lots are created by receipts, production reports and adjustments,
// never typed in, so the list is export-only (ADR-034: computed by postings).

interface Search extends ViewSearch {
  q: string;
  blocked: boolean;
  expiring: boolean;
  lot?: string | undefined;
}

export const Route = createFileRoute("/_app/master-data/lots")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = {
      q: typeof raw.q === "string" ? raw.q.slice(0, 40) : "",
      blocked: raw.blocked === true || raw.blocked === "true",
      expiring: raw.expiring === true || raw.expiring === "true",
    };
    if (typeof raw.lot === "string" && raw.lot) s.lot = raw.lot;
    // Links from before column sorting used ?sort=expiry.
    return { ...s, ...viewSearch(lotListColumns, raw.sort === "expiry" && !raw.s ? { ...raw, s: "expiry" } : raw) };
  },
  component: LotsPage,
});

function LotsPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { company } = useActiveCompany();
  const companyId = company?.id ?? "";
  const today = todayIso();

  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const params = { companyId, q: search.q, blocked: search.blocked, expiring: search.expiring, ...viewQuery(lotListColumns, search) };
  const query = useInfiniteQuery({ ...lotListQuery(params), enabled: !!companyId });

  const columns: Column<LotRow>[] = [
    {
      key: "lot",
      header: t("lots.colLot"),
      width: "minmax(130px,1fr)",
      cell: (r, selected) => <span className={cn("font-mono text-xs", selected && "font-semibold text-accent-ink")}>{r.lot_code}</span>,
    },
    {
      key: "item",
      header: t("lots.colItem"),
      width: "minmax(200px,2fr)",
      cell: (r) => (
        <span className="truncate">
          <span className="font-mono text-xs">{r.item_code}</span> <span className="text-ink-muted">{r.item_description}</span>
        </span>
      ),
    },
    {
      key: "expiry",
      header: t("lots.colExpiry"),
      width: "minmax(110px,0.8fr)",
      cell: (r) =>
        r.expiry_date ? (
          <span className={cn(r.expiry_date < today && "text-(--status-danger-fg)")}>{fmt.dayYear(r.expiry_date)}</span>
        ) : (
          <span className="text-ink-muted">—</span>
        ),
    },
    {
      key: "blocked",
      header: t("lots.colStatus"),
      width: "minmax(100px,0.6fr)",
      cell: (r) => (r.is_blocked ? <Badge tone="danger">{t("lots.blocked")}</Badge> : <span className="text-ink-muted">—</span>),
    },
    {
      key: "onHand",
      header: t("lots.colOnHand"),
      width: "minmax(110px,0.8fr)",
      align: "end",
      cell: (r) => (
        <span className="tabular">
          {fmt.qtyIn(r.on_hand, r.uom_decimals ?? 6)} <span className="text-ink-muted">{r.uom_code}</span>
        </span>
      ),
    },
  ];

  const exportColumns: ExportColumn<LotRow>[] = [
    { header: t("lots.colLot"), kind: "code", value: (r) => r.lot_code },
    { header: t("lots.colItem"), kind: "code", value: (r) => r.item_code },
    { header: t("lots.colDescription"), kind: "text", value: (r) => r.item_description },
    { header: t("lots.colExpiry"), kind: "date", value: (r) => r.expiry_date },
    { header: t("lots.produced"), kind: "date", value: (r) => r.production_date },
    { header: t("lots.supplierLot"), kind: "text", value: (r) => r.supplier_lot_code },
    { header: t("lots.blocked"), kind: "boolean", value: (r) => r.is_blocked },
    { header: t("lots.colOnHand"), kind: "number", value: (r) => r.on_hand, unit: (r) => r.uom_code },
    { header: t("lots.colUom"), kind: "code", value: (r) => r.uom_code },
  ];

  const listTools: ListTools<LotRow> = {
    id: "lots",
    title: t("lots.title"),
    export: { fileBase: "lots", sheet: t("lots.title"), columns: exportColumns, fetchPage: (cursor, signal) => listLots(params, cursor, signal, EXPORT_PAGE) },
  };

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.masterData.title")} <span aria-hidden="true">/</span> {t("nav.masterData.lots")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("lots.title")}</h1>
        </div>
      </header>

      <Toolbar search={search} setSearch={setSearch} />

      <div className="flex min-h-0 grow gap-4">
        <DataList
          label={t("lots.listLabel")}
          columns={columns}
          query={query}
          rowId={(r) => r.id}
          selectedId={search.lot}
          onSelect={(r) => setSearch({ lot: r.id })}
          onClear={() => setSearch({ lot: undefined })}
          onPrefetch={(r) => companyId && void queryClient.prefetchQuery(lotDetailQuery(r.id, companyId))}
          emptyTitle={t("lots.emptyTitle")}
          emptyBody={t("lots.emptyBody")}
          emptyAction={
            <button
              type="button"
              onClick={() => setSearch({ q: "", blocked: false, expiring: false, lot: undefined, ...noView })}
              className="h-9 rounded-xl border border-border px-3.5 text-[13px] font-medium hover:bg-surface-hover"
            >
              {t("list.clearFilters")}
            </button>
          }
          minWidth={680}
          tools={listTools}
          view={listView(lotListColumns, search, setSearch)}
        />
        <DetailPanel
          label={t("lots.detailLabel")}
          open={!!search.lot}
          placeholder={<DetailPlaceholder title={t("lots.selectPrompt")} hint={t("lots.selectPromptHint")} />}
        >
          {search.lot && companyId && (
            <LotPanel key={search.lot} lotId={search.lot} companyId={companyId} onClose={() => setSearch({ lot: undefined })} />
          )}
        </DetailPanel>
      </div>
    </div>
  );
}

function Toolbar({ search, setSearch }: { search: Search; setSearch: (p: Partial<Search>) => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState(search.q);

  useEffect(() => {
    if (text === search.q) return;
    const id = setTimeout(() => setSearch({ q: text }), 200);
    return () => clearTimeout(id);
  }, [text, search.q, setSearch]);

  const chip = (active: boolean) =>
    cn(
      "h-8 rounded-full border px-3 text-[13px] transition-colors",
      active ? "border-ink bg-ink font-medium text-surface" : "border-border bg-surface text-ink hover:bg-surface-hover",
    );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button type="button" aria-pressed={search.expiring} onClick={() => setSearch({ expiring: !search.expiring })} className={chip(search.expiring)}>
        {t("lots.filterExpiring")}
      </button>
      <button type="button" aria-pressed={search.blocked} onClick={() => setSearch({ blocked: !search.blocked })} className={chip(search.blocked)}>
        {t("lots.filterBlocked")}
      </button>
      <div className="grow" />
      <label className="flex h-9 min-w-[240px] items-center gap-2 rounded-xl border border-border bg-surface px-3 text-ink-muted focus-within:outline-2 focus-within:outline-focus">
        <Search size={15} aria-hidden="true" />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("lots.searchPlaceholder")}
          aria-label={t("lots.searchPlaceholder")}
          data-list-search
          className="min-w-0 grow bg-transparent font-mono text-[13px] text-ink uppercase outline-none placeholder:font-sans placeholder:normal-case placeholder:text-ink-muted"
        />
        {text && (
          <button type="button" aria-label={t("list.clearFilters")} onClick={() => setText("")} className="text-ink-muted">
            <X size={14} />
          </button>
        )}
      </label>
    </div>
  );
}
